import React, { useState } from 'react';

import { ArrowLeft, ArrowRight, CheckCircle, XCircle } from 'lucide-react';
import styled, { css } from 'styled-components';
import { useHistory } from 'react-router';
import { Question, Quiz } from '../../models/Quiz';
import useToast from '../../hooks/useToast';
import api from '../../services/api';
import useAuth from '../../hooks/useAuth';

export interface Props {
  quiz: Quiz;
}

export default function QuizPageComponent({ quiz }: Props) {
  const { user } = useAuth();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const { addToast } = useToast();
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [answers, setAnswers] = useState<{ [questionId: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { push } = useHistory();

  const currentQuestion = quiz.questions[currentQuestionIndex];

  const handleAnswerSelect = (answer: string) => {
    setSelectedAnswer(answer);
  };

  const handleNext = () => {
    if (selectedAnswer === null) return;

    setAnswers({
      ...answers,
      [currentQuestion.id]: selectedAnswer,
    });

    if (currentQuestionIndex < quiz.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedAnswer(null);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      setSelectedAnswer(answers[currentQuestionIndex - 1] || null);
    }
  };

  const handleSubmit = async () => {
    if (selectedAnswer === null) return;
    setIsSubmitting(true);

    await onSubmit();
  };

  const onSubmit = async () => {
    const answersWithLastOne = {
      ...answers,
      [currentQuestion.id]: selectedAnswer,
    };

    const body = {
      quizId: quiz.id,
      answers: answersWithLastOne,
      studentId: user.id,
    };

    const answerLength = Object.keys(body.answers).length;

    // Verifica se todas as perguntas foram respondidas
    if (answerLength < quiz.questions.length) {
      addToast({
        type: 'error',
        title: 'Erro ao enviar Quiz',
        description: 'Você deve responder todas as perguntas!',
      });
      return;
    }

    try {
      // Envia os dados para o backend
      const { data } = await api.post('quiz/complete', body);

      const note = data.note / 10;

      // Se o quiz foi completado com sucesso
      if (data.isCompleted) {
        addToast({
          type: 'success',
          title: 'Parabéns, você passou no teste!',
          description: `Sua nota foi ${note}!`,
        });
      }
      push(`/quiz`);
    } catch (err: any) {
      if (err.response && err.response.data) {
        const { internalCode } = err.response.data;

        // Manipula os diferentes tipos de erro
        switch (internalCode) {
          case 'ALREADY_COMPLETED_QUIZ':
            addToast({
              type: 'error',
              title: 'Quiz já concluído',
              description: 'Você não pode completar o Quiz mais de 2 vezes!',
            });
            break;
          case 'FAILED_QUIZ_FIRST':
            addToast({
              type: 'error',
              title: 'Você não passou no Quiz',
              description: 'Tente completar o Quiz novamente!',
            });
            break;
          case 'FAILED_QUIZ_SECOND':
            addToast({
              type: 'error',
              title: 'Infelizmente você não passou no Quiz',
              description: 'Continue estudando para os próximos testes!',
            });
            break;
          default:
            addToast({
              type: 'error',
              title: 'Erro ao completar Quiz',
              description:
                'Não foi possível completar o Quiz, tente novamente.',
            });
            break;
        }
        setIsSubmitting(false);
        push(`/quiz`);
      }
    }
  };

  return (
    <PageWrapper>
      <Container>
        <QuizCard>
          <Header>
            <QuizTitle>{quiz.title}</QuizTitle>
            <ProgressBadge>
              Questão {currentQuestionIndex + 1} de {quiz.questions.length}
            </ProgressBadge>
          </Header>

          <QuestionSection>
            <QuestionTitle>{currentQuestion.description}</QuestionTitle>
            <Options>
              <OptionButton
                selected={selectedAnswer === 'optionA'}
                onClick={() => handleAnswerSelect('optionA')}
              >
                {currentQuestion.optionA}
              </OptionButton>

              <OptionButton
                selected={selectedAnswer === 'optionB'}
                onClick={() => handleAnswerSelect('optionB')}
              >
                {currentQuestion.optionB}
              </OptionButton>
              <OptionButton
                selected={selectedAnswer === 'optionC'}
                onClick={() => handleAnswerSelect('optionC')}
              >
                {currentQuestion.optionC}
              </OptionButton>
              <OptionButton
                selected={selectedAnswer === 'optionD'}
                onClick={() => handleAnswerSelect('optionD')}
              >
                {currentQuestion.optionD}
              </OptionButton>
            </Options>
          </QuestionSection>

          <NavButtons>
            <NavButton
              onClick={handlePrevious}
              disabled={currentQuestionIndex === 0}
              variant="back"
            >
              <ArrowLeft size={16} /> Anterior
            </NavButton>

            {currentQuestionIndex === quiz.questions.length - 1 ? (
              <NavButton
                variant="next"
                disabled={!selectedAnswer || isSubmitting}
                onClick={handleSubmit}
              >
                {isSubmitting ? (
                  <>
                    Enviando <ArrowRight size={16} />
                  </>
                ) : (
                  <>
                    Enviar <CheckCircle size={16} />
                  </>
                )}
              </NavButton>
            ) : (
              <NavButton
                onClick={handleNext}
                disabled={!selectedAnswer}
                variant="next"
              >
                Próxima <ArrowRight size={16} />
              </NavButton>
            )}
          </NavButtons>
        </QuizCard>
      </Container>
    </PageWrapper>
  );
}

// Styled Components
const PageWrapper = styled.div`
  min-height: 100vh;
  background-color: ${({ theme }) => theme.colors.background};
  padding: 0.25rem 0;
`;

const Container = styled.div`
  max-width: 768px;
  margin: 0 auto;
  background-color: ${({ theme }) => theme.colors.background};
  padding: 0 1rem;
`;

const CardBase = css`
  background-color: #ffffff;
  border-radius: 1rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
`;

const QuizCard = styled.div`
  ${CardBase}
`;

const ResultsCard = styled(QuizCard)`
  padding: 2rem;
`;

const Title = styled.h1`
  font-size: 2.25rem;
  font-weight: bold;
  text-align: center;
  color: #7e22ce;
  margin-bottom: 1.5rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const QuizTitle = styled.h1`
  font-size: 1.875rem;
  font-weight: bold;
  color: #7e22ce;
`;

const ProgressBadge = styled.div`
  background-color: #f3e8ff;
  border-radius: 9999px;
  padding: 0.5rem 1rem;
  font-weight: bold;
`;

const QuestionSection = styled.div`
  background-color: #ebf8ff;
  border-radius: 1rem;
  padding: 1.5rem;
  margin-bottom: 2rem;
`;

const QuestionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: bold;
  color: #1e3a8a;
  margin-bottom: 1.5rem;
`;

const Options = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const OptionButton = styled.button<{ selected: boolean }>`
  width: 100%;
  text-align: left;
  padding: 1rem;
  border-radius: 1rem;
  font-size: 1.125rem;
  font-weight: 500;
  transition: background-color 0.2s;
  background-color: ${props => (props.selected ? '#7e22ce' : '#ffffff')};
  color: ${props => (props.selected ? '#ffffff' : '#000000')};
  border: none;
  cursor: pointer;

  &:hover {
    background-color: ${props => (props.selected ? '#6b21a8' : '#f3f4f6')};
  }
`;

const NavButtons = styled.div`
  display: flex;
  justify-content: space-between;
`;

const NavButton = styled.button<{
  variant: 'back' | 'next';
  disabled: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 1.2rem;
  font-size: 1.125rem;
  font-weight: bold;
  border: none;
  cursor: pointer;

  ${props =>
    props.disabled &&
    css`
      background-color: #e5e7eb;
      color: #9ca3af;
      cursor: not-allowed;
    `}

  ${props =>
    !props.disabled &&
    props.variant === 'back' &&
    css`
      background-color: #3b82f6;
      color: #ffffff;
      &:hover {
        background-color: #2563eb;
      }
    `}
  
    ${props =>
    !props.disabled &&
    props.variant === 'next' &&
    css`
      background-color: #10b981;
      color: #ffffff;
      &:hover {
        background-color: #059669;
      }
    `}
`;

const Encouragement = styled.div`
  background-color: #fef3c7;
  border-radius: 1rem;
  padding: 1.5rem;
  text-align: center;
  margin-bottom: 2rem;
`;

const EncouragementTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: bold;
  color: #b45309;
  margin-bottom: 0.5rem;
`;

const Score = styled.p`
  font-size: 2rem;
  font-weight: bold;
  color: #7e22ce;
`;

const ProgressBarWrapper = styled.div`
  width: 100%;
  background-color: #e5e7eb;
  border-radius: 9999px;
  height: 1.5rem;
  margin-top: 1rem;
`;

const ProgressBar = styled.div<{ fill: number }>`
  height: 100%;
  border-radius: 9999px;
  background-color: #10b981;
  width: ${props => props.fill}%;
`;

const ResultItem = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 1rem;
  padding: 1rem;
  margin-bottom: 1rem;
`;

const QuestionText = styled.p`
  font-weight: bold;
  font-size: 1.125rem;
  margin-bottom: 0.5rem;
`;

const AnswerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const AnswerText = styled.p`
  font-weight: 500;
`;

const CorrectAnswer = styled.p`
  color: #059669;
  font-weight: 500;
  margin-top: 0.5rem;
`;

const BackLink = styled.button`
  background-color: #7e22ce;
  color: #ffffff;
  padding: 1rem 2rem;
  border-radius: 9999px;
  font-size: 1.25rem;
  font-weight: bold;
  border: none;
  cursor: pointer;
  &:hover {
    background-color: #6b21a8;
  }
`;
